import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DataGridPro, GridActionsCellItem, koKR } from "@mui/x-data-grid-pro";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { ConfirmDialog } from "../dialog";
import { CustomNoRowsOverlay } from "../datagrid";
import { getGridHeight } from "../../constants/gridHeights";
import * as gcomponentActions from "../../store/gcomponent";
import * as g04docuGCertificationActions from "../../store/g04docuGCertification";
import * as gmaterialSlotActions from "../../store/gmaterialSlot";
import GMaterialSlotDialog from "./GMaterialSlotDialog";

const GRID_HEIGHT = getGridHeight("ONE_LINE_LAYER");
const theme = createTheme();

const GMaterialSlotManagement = () => {
  const dispatch = useDispatch();
  const materialSlots = useSelector((state) => state.gmaterialSlot.materialSlots);
  const components = useSelector((state) => state.gcomponent.gcomponents);
  const [certifications, setCertifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [modify, setModify] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [params, setParams] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(100);

  const reload = async () => {
    setLoaded(false);
    await dispatch(gmaterialSlotActions.selectAll());
    setLoaded(true);
  };

  useEffect(() => {
    dispatch(gcomponentActions.selectAll());
    g04docuGCertificationActions.selectAllByQueryDirect().then((list) => setCertifications(list || []));
    reload();
  }, [dispatch]);

  const handleSave = async (slot) => {
    if (modify) {
      await dispatch(gmaterialSlotActions.modify(slot));
    } else {
      await dispatch(gmaterialSlotActions.create(slot));
    }
    setOpen(false);
    setSelected(null);
    await reload();
  };

  const columns = [
    { field: "name", headerName: "이름", width: 180 },
    { field: "code", headerName: "코드", width: 200 },
    {
      field: "details",
      headerName: "칸",
      flex: 1,
      minWidth: 280,
      valueGetter: (params) => (params.row.details || []).map((detail) => detail.gcomponentCode).join(", "),
    },
    {
      field: "certifications",
      headerName: "인증규격",
      flex: 1,
      minWidth: 280,
      valueGetter: (params) => (params.row.certifications || []).map((cert) => cert.certificationName).join(", "),
    },
    {
      field: "actions",
      type: "actions",
      headerName: <Tooltip title="수정/삭제" followCursor><Box>기능</Box></Tooltip>,
      width: 70,
      getActions: (rowParams) => [
        <GridActionsCellItem
          key="edit"
          icon={<Edit />}
          label="수정"
          showInMenu
          onClick={() => {
            setModify(true);
            setSelected(rowParams.row);
            setOpen(true);
          }}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="삭제"
          showInMenu
          onClick={() => {
            setRemoveId(rowParams.id);
            setParams(rowParams);
            setConfirmOpen(true);
          }}
        />,
      ],
    },
  ];

  return (
    <ThemeProvider theme={theme}>
    <Container component="main" maxWidth="false">
      <CssBaseline />
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} />
          <Grid item xs={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              startIcon={<Add />}
              onClick={() => {
                setModify(false);
                setSelected(null);
                setOpen(true);
              }}
            >
              등록하기
            </Button>
          </Grid>
        </Grid>
        <div style={{ height: GRID_HEIGHT.FULL, width: "100%" }}>
          <DataGridPro
            localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
            sx={{ cursor: "pointer", fontSize: "0.85em" }}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: LinearProgress,
            }}
            headerHeight={38}
            rowHeight={34}
            loading={!loaded}
            rows={materialSlots || []}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 20, 50, 100]}
            pagination
            onRowDoubleClick={(rowParams) => {
              setModify(true);
              setSelected(rowParams.row);
              setOpen(true);
            }}
          />
        </div>
      </Box>
      <GMaterialSlotDialog
        open={open}
        modify={modify}
        slot={selected}
        components={components || []}
        certifications={certifications}
        onClose={() => setOpen(false)}
        onSave={handleSave}
      />
      <ConfirmDialog
        removeId={removeId}
        title="삭제"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={async (id) => {
          await dispatch(gmaterialSlotActions.remove(id));
          await reload();
        }}
        onCancel={() => {}}
      >
        {`"${params && params.row && params.row.name || ""}(아이디 : ${params && params.id || ""})"를 삭제하시겠습니까?`}
      </ConfirmDialog>
    </Container>
    </ThemeProvider>
  );
};

export default GMaterialSlotManagement;
